import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, ChevronLeft, ChevronRight, Smartphone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SOLUTION_DETAILS } from '../mock';

function ImageCarousel({ images, alt }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i === images.length - 1 ? 0 : i + 1));
    }, 4000);
    return () => clearInterval(timer);
  }, [index, images.length]);

  if (!images || images.length === 0) return null;
  const getImgClass = (url) => {
    const isProduct = url.includes('scooter_green') || url.includes('scooter_black');
    return `w-full h-full ${isProduct ? 'object-contain bg-white p-1' : 'object-cover'}`;
  };

  if (images.length === 1) {
    return (
      <div className="w-full h-full relative">
        <img src={images[0]} alt={alt} className={getImgClass(images[0])} />
      </div>
    );
  }

  const prev = (e) => {
    e.stopPropagation();
    setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  };

  const next = (e) => {
    e.stopPropagation();
    setIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  };

  return (
    <div className="relative w-full h-full group overflow-hidden bg-brand-dark/5">
      <AnimatePresence mode="wait">
        <motion.img
          key={index}
          src={images[index]}
          alt={`${alt} ${index + 1}`}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`${getImgClass(images[index])} absolute inset-0`}
        />
      </AnimatePresence>

      {/* Navigation Arrows */}
      <button
        onClick={prev}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white text-brand-dark flex items-center justify-center shadow-md transition-all opacity-0 group-hover:opacity-100 z-20"
        aria-label="Previous image"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        onClick={next}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white text-brand-dark flex items-center justify-center shadow-md transition-all opacity-0 group-hover:opacity-100 z-20"
        aria-label="Next image"
      >
        <ChevronRight size={18} />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5 z-20">
        {images.map((_, idx) => (
          <button
            key={idx}
            onClick={(e) => {
              e.stopPropagation();
              setIndex(idx);
            }}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === index ? 'bg-brand-lime w-4' : 'bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function OfferingPage() {
  const imageVariants = (reverse) => ({
    hidden: { opacity: 0, x: reverse ? 50 : -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
    }
  });

  const textVariants = (reverse) => ({
    hidden: { opacity: 0, x: reverse ? -50 : 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.05 }
    }
  });

  return (
    <>
      <section className="pt-36 pb-12 bg-brand-mint relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative max-w-4xl mx-auto px-6 text-center"
        >
          <h1 className="heading-display text-4xl md:text-6xl text-brand-dark">Mobility Verticals. Powered by One Platform.</h1>
          <p className="mt-4 text-brand-dark/70 max-w-2xl mx-auto">One EV platform powering dedicated mobility verticals across last-mile delivery, campuses and everyday commute.</p>
        </motion.div>
      </section>

      <section className="section-pad bg-white">
        <div className="max-w-6xl mx-auto px-6 space-y-24 overflow-hidden">
          {SOLUTION_DETAILS.map((s, idx) => {
            const Icon = s.icon;
            const reverse = idx % 2 === 1;
            return (
              <div key={s.id} id={s.id} className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center scroll-mt-24">
                <motion.div 
                  variants={imageVariants(reverse)}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  className={`relative ${reverse ? 'md:order-2' : ''}`}
                >
                  <div className="rounded-3xl overflow-hidden h-80 md:h-96 bg-brand-mint shadow-md group">
                    <ImageCarousel images={s.images || [s.image]} alt={s.title} />
                  </div>
                  {s.badge && <span className="absolute top-4 left-4 bg-brand-lime text-brand-dark text-xs font-bold px-3 py-1 rounded-full">{s.badge}</span>}
                </motion.div>
                
                <motion.div 
                  variants={textVariants(reverse)}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  className={reverse ? 'md:order-1' : ''}
                >
                  <div className="w-12 h-12 rounded-xl bg-brand-mint text-brand-dark flex items-center justify-center shadow-sm"><Icon size={22} /></div>
                  <h2 className="heading-display text-3xl md:text-4xl text-brand-dark mt-4">{s.title}</h2>
                  {s.tagline && <p className="text-brand-green font-semibold text-sm mt-2">{s.tagline}</p>}
                  <p className="text-brand-dark/65 mt-3 leading-relaxed">{s.intro}</p>

                  {s.builtFor ? (
                    <div className="mt-6">
                      <h3 className="font-bold text-brand-dark text-xs uppercase tracking-wider">Built For</h3>
                      <p className="text-sm text-brand-dark/80 font-medium mt-1 leading-relaxed">{s.builtFor.join(' · ')}</p>
                    </div>
                  ) : (
                    s.benefits && (
                      <>
                        <h3 className="font-semibold text-brand-dark mt-6 mb-2 text-sm uppercase tracking-wider">Key Benefits</h3>
                        <ul className="space-y-2">
                          {s.benefits.map((b) => (
                            <li key={b} className="flex items-start gap-2 text-sm text-brand-dark/80"><CheckCircle2 size={15} className="text-brand-green mt-0.5" />{b}</li>
                          ))}
                        </ul>
                      </>
                    )
                  )}

                  {s.whatWeEnable ? (
                    <div className="mt-6">
                      <h3 className="font-bold text-brand-dark text-xs uppercase tracking-wider mb-2">{s.featuresTitle || 'What We Enable'}</h3>
                      <ul className="space-y-1.5">
                        {s.whatWeEnable.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-brand-dark/85">
                            <span className="text-brand-green font-bold text-sm shrink-0 select-none">✓</span> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    s.steps && (
                      <>
                        <h3 className="font-semibold text-brand-dark mt-6 mb-3 text-sm uppercase tracking-wider">How It Works</h3>
                        <div className="space-y-2">
                          {s.steps.map((step, i) => (
                            <div key={i} className="flex gap-3 items-start">
                              <span className="w-6 h-6 shrink-0 rounded-full bg-brand-dark text-brand-lime text-xs font-bold flex items-center justify-center">{i+1}</span>
                              <span className="text-sm text-brand-dark/80">{step}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )
                  )}

                  <div className="flex flex-wrap gap-4 mt-8">
                    <Link to={`/contact?type=${s.id}`} className="btn-dark-pill">{s.cta || 'Get Started'} <ArrowRight size={16} /></Link>
                    {s.id === 'gearup' && (
                      <a 
                        href="https://play.google.com/store/apps/details?id=in.edrives.app.customer&pli=1" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brand-lime text-brand-dark font-semibold shadow-sm hover:shadow-md hover:bg-brand-lime/90 hover:-translate-y-0.5 transition-all text-sm"
                      >
                        <Smartphone size={16} /> Download the App
                      </a>
                    )}
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-20 bg-brand-mint relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative max-w-4xl mx-auto px-6 text-center"
        >
          <h2 className="heading-display text-3xl md:text-5xl text-brand-dark font-bold">Powering Mobility, Wherever It’s Needed.</h2>
          <p className="text-brand-dark/75 mt-4 text-base md:text-lg max-w-2xl mx-auto">
            From delivery networks and campuses to everyday commute, E Drives builds dedicated electric mobility services on one connected platform.
          </p>
          <Link to="/contact" className="btn-dark-pill mt-8">Connect With Us <ArrowRight size={16} /></Link>
        </motion.div>
      </section>
    </>
  );
}
